import { config } from '@cfg';
import { Button } from '@components/Button';
import {
  PhoneInputField,
  ErrorMessage,
  FormLabel,
  InputField,
  TextArea,
} from '@components/FormFields';
import { CreateLoftyLeadDto } from '@engelvoelkersamericas/api';
import handleRecaptchaAndLofty from '@util/recaptcha';
import { Checkbox } from 'antd';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import { sanitizedNumber } from 'util/phoneNumber';
import {
  CustomButtonWrapper,
  DisclaimerAndErrorContainer,
  DisclaimerContainer,
  DisclaimerText,
  FieldWrapper,
  FieldWrapperDashboard,
  FormFinished,
  Header,
  ReadMore,
  StyledForm,
  Text,
} from './styles';

const messageSuccessHeader = 'Thanks for your message.';
const messageSuccessText = "We'll be in touch as soon as possible";
const messageFailedHeader = 'Form submission faild!';
const messageFailedText = 'Please try again later';

export interface DefaultEmbeddedFormProps {
  ctaText: string;
  disclaimer: string;
  userData?: any;
  extraGutterWidth?: boolean;
  chimeId: string;
  chimeType: string;
  ownershipId?: string;
  ownershipScope?: string;
  welcomeEmail?: boolean;
  zipCode?: string;
  isStackedImageForm: boolean;
  setFormSuccess: (val: boolean | null) => void;
}

const DefaultEmbeddedForm: FC<DefaultEmbeddedFormProps> = observer(
  ({
    ctaText,
    disclaimer,
    userData,
    extraGutterWidth = false,
    chimeId,
    chimeType,
    ownershipId,
    ownershipScope,
    welcomeEmail,
    zipCode,
    isStackedImageForm,
    setFormSuccess,
  }) => {
    const locale = useLocale();
    const t = useTranslations();
    const { appStore, shopStore } = useStores();
    const { validSubdomainData } = appStore;
    const { shopChimeId } = shopStore;

    const [isSent, setIsSent] = useState(false);
    const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
    const [firstNameError, setFirstNameError] = useState<string | null>(null);
    const [lastNameError, setLastNameError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [messageError, setMessageError] = useState<string | null>(null);
    const [checkedBoxError, setCheckedBoxError] = useState<string | null>(null);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [url, setURL] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const pathname = usePathname();

    useEffect(() => {
      setURL(`${window.location.origin}${pathname}`);
      setIsLoaded(true);
    }, []);

    const sendForm = () => {
      setIsSent(true);
    };
    const [phoneNumber, setPhoneNumber] = useState<string>('');

    const toggleReadMore = () => {
      setIsOpen(!isOpen);
    };

    const handlePhoneInputChange = (e) => {
      setPhoneNumber(e ?? '');
    };
    const handleInputChange = (e) => {
      if (e?.target?.id == 'firstName') {
        setFirstNameError('');
      }

      if (e?.target?.id == 'lastName') {
        setLastNameError('');
      }

      if (e?.target?.id == 'email') {
        setEmailError('');
      }

      if (e?.target?.id == 'message') {
        setMessageError('');
      }

      if (e?.target?.id == 'checkedBox') {
        setCheckedBoxError('');
      }
    };

    const handleSubmit = async (event) => {
      // Stop the form from submitting and refreshing the page.
      event.preventDefault();
      setFirstNameError('');
      setLastNameError('');
      setEmailError('');
      setPhoneError('');
      setMessageError('');
      setCheckedBoxError('');

      // Get data from the form.
      const data = {
        firstName: userData ? userData?.firstName : event?.target?.firstName.value,
        lastName: userData ? userData?.lastName : event?.target?.lastName.value,
        phone: userData ? userData?.phone : event?.target?.phone.value,
        email: userData ? userData?.email : event?.target?.email.value,
        message: event?.target?.message.value,
        checkedBox: event?.target?.checkedBox?.checked,
      };

      //validations
      let formIsValid = true;
      setFirstNameError('');
      if (!data.firstName) {
        formIsValid = false;
        setFirstNameError('First Name cannot be empty');
      }

      if (!data.lastName) {
        setLastNameError('');
        formIsValid = false;
        setLastNameError('Last Name cannot be empty');
      }

      //Email
      if (!data.email) {
        formIsValid = false;
        setEmailError('Email cannot be empty');
      }

      if (typeof data.email !== 'undefined') {
        let lastAtPos = data.email.lastIndexOf('@');
        let lastDotPos = data.email.lastIndexOf('.');

        if (
          !(
            lastAtPos < lastDotPos &&
            lastAtPos > 0 &&
            data.email.indexOf('@@') == -1 &&
            lastDotPos > 2 &&
            data.email.length - lastDotPos > 2
          )
        ) {
          formIsValid = false;
          setEmailError('Email is not valid');
        }
      }

      if (!data.phone) {
        formIsValid = false;
        setPhoneError('Phone cannot be empty');
      }
      //Message
      if (!data.message) {
        formIsValid = false;
        setMessageError('Message cannot be empty');
      }
      if (!data.checkedBox) {
        formIsValid = false;
        setCheckedBoxError('Please check to submit this form');
      }

      if (formIsValid) {
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setPhoneError('');
        setMessageError('');
        setCheckedBoxError('');

        // Send the data to the server in JSON format.
        const chimeObj: CreateLoftyLeadDto = {
          userFirstName: data.firstName,
          userLastName: data.lastName,
          userPhone: sanitizedNumber(data.phone),
          userEmail: data.email,
          message: data.message,
          chimeId: chimeId ? parseInt(chimeId, 10) : undefined,
          type: chimeType,
          ownershipId: ownershipId ? parseInt(ownershipId, 10) : undefined,
          welcomeEmail: !!welcomeEmail,
        };
        if (zipCode) {
          chimeObj.zipcode = zipCode;
        }
        if (url) {
          chimeObj.url = url;
        }

        // If we are on a shop subdomain, then set the ownershipId to the shop's chimeId
        if (
          validSubdomainData &&
          validSubdomainData.type?.toLowerCase() !== 'advisor' &&
          chimeType.toLowerCase() === 'shop' &&
          shopChimeId
        ) {
          chimeObj.ownershipId = shopChimeId;
        }
        if (chimeObj.ownershipId) {
          chimeObj.ownershipScope = ownershipScope || 'OFFICE';
        }

        await handleRecaptchaAndLofty(
          chimeObj,
          sendForm,
          setRecaptchaError,
          setIsSuccess,
          setFormSuccess,
        );
      }
    };

    return (
      <>
        {isSent && isSuccess !== undefined ? (
          <FormFinished datat-testid="contact-form-finished">
            <Header data-testid="form-finished-header" $isSent={isSent}>
              {isSuccess ? messageSuccessHeader : messageFailedHeader}
            </Header>
            <Text data-testid="form-finished-text" $isSent={isSent}>
              {isSuccess ? messageSuccessText : messageFailedText}
            </Text>
          </FormFinished>
        ) : (
          isLoaded && (
            <StyledForm
              $gutterWidth={extraGutterWidth}
              $isStackedImageForm={isStackedImageForm}
              onSubmit={handleSubmit}
              data-testid="contact-form"
            >
              {!userData && (
                <>
                  <FieldWrapper data-testid="first-name" style={{ flexGrow: 1 }}>
                    <FormLabel data-testid="first-name-label" htmlFor="firstName" $isContactForm>
                      {t('First Name')}
                    </FormLabel>
                    <InputField
                      id="firstName"
                      data-testid="first-name-input"
                      style={{ height: !isStackedImageForm ? '48px' : undefined }}
                      placeholder={t('First Name')}
                      type="text"
                      $isError={!!firstNameError}
                      onChange={(e) => handleInputChange(e)}
                    />
                    {firstNameError && (
                      <ErrorMessage dataTestId="first-name-error" message={firstNameError} />
                    )}
                  </FieldWrapper>

                  <FieldWrapper data-testid="last-name" style={{ flexGrow: 1 }}>
                    <FormLabel data-testid="last-name-label" htmlFor="lastName" $isContactForm>
                      {t('Last Name')}
                    </FormLabel>
                    <InputField
                      id="lastName"
                      data-testid="last-name-input"
                      style={{ height: !isStackedImageForm ? '48px' : undefined }}
                      placeholder={t('Last Name')}
                      $isError={!!lastNameError}
                      onChange={(e) => handleInputChange(e)}
                    />
                    {lastNameError && (
                      <ErrorMessage dataTestId="last-name-error" message={lastNameError} />
                    )}
                  </FieldWrapper>

                  <FieldWrapper data-testid="phone">
                    <FormLabel data-testid="phone-label" htmlFor="phone" $isContactForm>
                      {t('Phone')}
                    </FormLabel>
                    <PhoneInputField
                      id="phone"
                      placeholder="(123)-456-7890"
                      locale={locale}
                      isError={phoneError != '' ? true : false}
                      onChange={(e) => handlePhoneInputChange(e)}
                      value={phoneNumber}
                    />
                    {phoneError && <ErrorMessage dataTestId="phone-error" message={phoneError} />}
                  </FieldWrapper>

                  <FieldWrapper data-testid="email">
                    <FormLabel data-testid="email-label" htmlFor="email" $isContactForm>
                      {t('Email')}
                    </FormLabel>
                    <InputField
                      id="email"
                      data-testid="email-input"
                      style={{ height: !isStackedImageForm ? '48px' : undefined }}
                      placeholder={'you@emailaddress.com'}
                      onChange={(e) => handleInputChange(e)}
                      $isError={!!emailError}
                    />
                    {emailError && <ErrorMessage dataTestId="email-error" message={emailError} />}
                  </FieldWrapper>
                </>
              )}

              <FieldWrapperDashboard data-testid="message">
                <FormLabel data-testid="message-label" $isContactForm>
                  {t('Message')}
                </FormLabel>
                <TextArea
                  id="message"
                  data-testid="message-input"
                  style={{ height: !isStackedImageForm ? '125px' : undefined }}
                  placeholder={t('contactMessagePlaceholder')}
                  maxLength={250}
                  onChange={(e) => handleInputChange(e)}
                />
                {messageError && <ErrorMessage dataTestId="message-error" message={messageError} />}
              </FieldWrapperDashboard>
              <DisclaimerAndErrorContainer $isStackedImageForm={isStackedImageForm}>
                <DisclaimerContainer $isStackedImageForm={isStackedImageForm}>
                  <Checkbox
                    id="checkedBox"
                    data-testid="disclaimer-checkbox"
                    style={{ display: 'flex', alignSelf: 'start' }}
                    key="data-processing-consent"
                    title="data-processing-consent"
                  />
                  <DisclaimerText
                    style={{
                      marginBottom: checkedBoxError !== '' ? '0' : undefined,
                    }}
                    data-testid="disclaimer-text"
                    $isStackedImageForm={isStackedImageForm}
                  >
                    <span>{!isOpen ? disclaimer.slice(0, 251) + '...' : disclaimer}</span>
                    <ReadMore data-testid="read-more" onClick={toggleReadMore}>
                      {isOpen ? t('Read Less') : t('Read More')}
                    </ReadMore>
                  </DisclaimerText>
                </DisclaimerContainer>
                {checkedBoxError && (
                  <ErrorMessage dataTestId="disclaimer-error" message={checkedBoxError} />
                )}
                {recaptchaError && (
                  <ErrorMessage
                    data-testid="recaptcha-error"
                    style={{ paddingTop: checkedBoxError ? '4px' : undefined }}
                    message={recaptchaError}
                  />
                )}
                <CustomButtonWrapper>
                  <Button
                    type="submit"
                    className="g-recaptcha"
                    dataTestId="submit-button"
                    data-sitekey={config.recaptcha.key}
                    data-callback="onSubmit"
                    data-action="submit"
                    label={ctaText ? ctaText : t('Send Message')}
                    variant={'secondaryRed'}
                    arrow={false}
                    disabled={chimeId == null ? true : false}
                  />
                </CustomButtonWrapper>
              </DisclaimerAndErrorContainer>
            </StyledForm>
          )
        )}
      </>
    );
  },
);

export default DefaultEmbeddedForm;
